import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, finalize, Observable, of, shareReplay, switchMap, throwError } from "rxjs";
import { UserHttpService } from "../services/user.http.service";

let refreshRequest$: Observable<void> | null = null;
let csrfRequest$: Observable<any> | null = null;
let csrfToken: string | null = null;
const unsafeMethods = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Legacy bearer-token auth kept temporarily for cleanup. Cookie auth now uses HttpOnly cookies.
// function addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
//     return req.clone({
//         setHeaders: {
//             Authorization: `Bearer ${token}`,
//         },
//     });
// }

function addCredentials(req: HttpRequest<unknown>): HttpRequest<unknown> {
    return req.clone({ withCredentials: true });
}

function getCookieValue(name: string): string {
    return document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(`${name}=`))
        ?.split('=')
        .slice(1)
        .join('=') ?? '';
}

function addCredentialsAndCsrf(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = csrfToken || getCookieValue('csrfToken');

    if (!token || req.headers.has('X-CSRF-Token')) {
        return addCredentials(req);
    }

    return req.clone({
        withCredentials: true,
        setHeaders: {
            'X-CSRF-Token': decodeURIComponent(token),
        },
    });
}

function needsCsrf(req: HttpRequest<unknown>, httpService: UserHttpService): boolean {
    return unsafeMethods.has(req.method) &&
        req.url.startsWith(httpService.apiUrl) &&
        !req.url.includes('/auth/csrf');
}

function isCsrfFailure(error: HttpErrorResponse): boolean {
    return error.status === 403 && (
        error.error?.code === 'INVALID_CSRF_TOKEN' ||
        String(error.error?.message || '').toLowerCase().includes('csrf token')
    );
}

function ensureCsrfToken(httpService: UserHttpService): Observable<string> {
    if (!csrfRequest$) {
        csrfRequest$ = httpService.getCsrfToken().pipe(
            finalize(() => {
                csrfRequest$ = null;
            }),
            shareReplay(1),
        );
    }

    return csrfRequest$.pipe(
        switchMap((res: any) => {
            csrfToken = String(res?.csrfToken || '');
            return of(csrfToken);
        }),
    );
}

function prepareRequest(req: HttpRequest<unknown>, httpService: UserHttpService): Observable<HttpRequest<unknown>> {
    if (!needsCsrf(req, httpService) || csrfToken || getCookieValue('csrfToken')) {
        return of(addCredentialsAndCsrf(req));
    }

    return ensureCsrfToken(httpService).pipe(
        switchMap(() => of(addCredentialsAndCsrf(req))),
    );
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
    const httpService = inject(UserHttpService);
    const isRefreshRequest = req.url.includes('/auth/refresh');
    const isAuthFlowRequest = req.url.includes('/auth/login') ||
        req.url.includes('/auth/oauth') ||
        req.url.includes('/auth/2fa/2fa-login') ||
        req.url.includes('/auth/2fa/recovery/verify') ||
        req.url.includes('/auth/logout');
    // Legacy bearer-token auth kept temporarily for cleanup. Cookie auth now uses HttpOnly cookies.
    // const token = httpService.getToken();
    // const authReq = token && !isRefreshRequest ? addAuthHeader(req, token) : req;
    const isCsrfRequest = req.url.includes('/auth/csrf');

    if (isCsrfRequest) {
        return next(addCredentials(req));
    }

    return prepareRequest(req, httpService).pipe(
        switchMap((authReq) => next(authReq)),
        catchError((error: HttpErrorResponse) => {
            if (isCsrfFailure(error) && needsCsrf(req, httpService)) {
                csrfToken = null;
                return ensureCsrfToken(httpService).pipe(
                    switchMap(() => next(addCredentialsAndCsrf(req))),
                );
            }

            if (isRefreshRequest || isAuthFlowRequest || error.status !== 401) {
                return throwError(() => error);
            }

            if (!refreshRequest$) {
                refreshRequest$ = httpService.refreshAccessToken().pipe(
                    finalize(() => {
                        refreshRequest$ = null;
                    }),
                    shareReplay(1),
                );
            }

            return refreshRequest$.pipe(
                switchMap(() =>
                    prepareRequest(req, httpService).pipe(
                        switchMap((retryReq) =>
                            next(retryReq).pipe(
                                catchError((retryError) =>
                                    throwError(() => ({ __retryError: true, retryError })),
                                ),
                            ),
                        ),
                    ),
                ),
                catchError((refreshError) => {
                    // Do not logout on business 401s from retried requests (e.g. wrong current password).
                    if ((refreshError as any)?.__retryError) {
                        return throwError(() => (refreshError as any).retryError);
                    }

                    // Logout only when refresh itself fails.
                    httpService.logout();
                    return throwError(() => refreshError);
                }),
            );
        }),
    );
};

// token version
// import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
// import { inject } from "@angular/core";
// import { catchError, finalize, Observable, shareReplay, switchMap, throwError } from "rxjs";
// import { UserHttpService } from "../services/user.http.service";

// let refreshRequest$: Observable<string> | null = null;

// function addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
//     return req.clone({
//         setHeaders: {
//             Authorization: `Bearer ${token}`,
//         },
//     });
// }

// export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
//     const httpService = inject(UserHttpService);
//     const isRefreshRequest = req.url.includes('/auth/refresh');
//     const token = httpService.getToken();
//     const authReq = token && !isRefreshRequest ? addAuthHeader(req, token) : req;

//     return next(authReq).pipe(
//         catchError((error: HttpErrorResponse) => {
//             if (isRefreshRequest || error.status !== 401) {
//                 return throwError(() => error);
//             }

//             if (!refreshRequest$) {
//                 refreshRequest$ = httpService.refreshAccessToken().pipe(
//                     finalize(() => {
//                         refreshRequest$ = null;
//                     }),
//                     shareReplay(1),
//                 );
//             }

//             return refreshRequest$.pipe(
//                 switchMap((newToken) =>
//                     next(addAuthHeader(req, newToken)).pipe(
//                         catchError((retryError) =>
//                             throwError(() => ({ __retryError: true, retryError })),
//                         ),
//                     ),
//                 ),
//                 catchError((refreshError) => {
//                     // Do not logout on business 401s from retried requests (e.g. wrong current password).
//                     if ((refreshError as any)?.__retryError) {
//                         return throwError(() => (refreshError as any).retryError);
//                     }

//                     // Logout only when refresh itself fails.
//                     httpService.logout();
//                     return throwError(() => refreshError);
//                 }),
//             );
//         }),
//     );
// };
